import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Pill, the inert badge that labels a fact about a card, an order or a row.
 *
 * Source selectors: `system.css` `.dsk-pill` + `--success`, `--warning`,
 * `--info`, `--error`, `--ink`, plus the `.dsk-fit` overflow helper.
 *
 * FOUR STATUS TONES, NOT THREE. `apps/web/src/utils/orderStatus.js` sorts every
 * status the API can return into four colour buckets, `warning`, `info`,
 * `success` and `error`, plus `primary` on `approved` alone. `info` is the
 * accent, because a live state is not an outcome, and `primary` maps to the ink
 * pill because an approval is the system stating a decision it made. Those two
 * names are deliberately the ones `getStatusColor()` returns, so a caller can
 * pass its result straight through without a translation table.
 *
 * Doctrine enforced:
 * - Status tones are the shared `color-mix` recipe: a tinted background at
 *   `--ds-status-tint-amount` and text mixed toward `--text-primary`. Never a
 *   flat saturated fill, which stops being readable in dark.
 * - `ink` is the emphatic tone and it is a COMMAND colour, so it is for the
 *   availability badge and other system statements, not for decoration.
 * - A pill NEVER wraps. Two-line pills break a grid's vertical rhythm and every
 *   card ends up a different height.
 * - A pill is inert: it renders a `<span>`, never a button. If it can be
 *   clicked to change the result set it is a `Chip`, not a pill.
 *
 * `fit` opts a long pill into `.dsk-fit`, which the desktop behaviour script
 * shrinks a step at a time down to a 9px floor rather than truncating. Without
 * that script running, `.dsk-fit` still gives the pill honest clipping.
 */
export const Pill = React.forwardRef(function Pill(
  { tone = 'default', fit = false, className, ...rest },
  ref,
) {
  const toneClass = {
    default: null,
    success: 'dsk-pill--success',
    warning: 'dsk-pill--warning',
    info: 'dsk-pill--info',
    error: 'dsk-pill--error',
    ink: 'dsk-pill--ink',
    /* `primary` is the name orderStatus.js uses for the approved bucket. It is
       an alias onto ink, not a sixth colour. */
    primary: 'dsk-pill--ink',
  }[tone];

  return (
    <span ref={ref} className={cn('dsk-pill', toneClass, fit && 'dsk-fit', className)} {...rest} />
  );
});
Pill.displayName = 'Pill';

export default Pill;
